import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { isFailingResult } from "@/lib/examStatus";
// xlsx loaded dynamically to reduce bundle size
import type { ResultContext } from "./ResultSetup";

interface Props {
  context: ResultContext;
}

export function TemplateGenerator({ context }: Props) {
  const { toast } = useToast();
  const [generating, setGenerating] = useState(false);
  const isReady = context.semester && context.selectedClass && context.examType && context.academicYear;
  const isPre = context.studentSource === "pre_registered";

  const isReupload = context.mode === "reupload";

  // Signed-up students (include any "active" variant)
  const { data: signedUpStudents = [] } = useQuery({
    queryKey: ["template-students", context.selectedClass],
    queryFn: async () => {
      if (!context.selectedClass) return [];
      const { data } = await supabase
        .from("students")
        .select("id, first_name, last_name, student_number, form_iv_index, status")
        .eq("class", context.selectedClass)
        .order("first_name");
      return (data ?? []).filter((s: any) =>
        typeof s.status === "string" && s.status.toLowerCase().includes("active")
      );
    },
    enabled: !!context.selectedClass && !isPre,
  });

  // Pre-registered students
  const { data: preRegStudents = [] } = useQuery({
    queryKey: ["template-prereg-students", context.selectedClass, context.semester],
    queryFn: async () => {
      if (!context.selectedClass) return [];
      const { data } = await (supabase as any)
        .from("student_pre_registrations_exam_safe")
        .select("id, full_name, form_iv_index, course, semester, class")
        .eq("class", context.selectedClass)
        .eq("semester", context.semester)
        .order("full_name");
      return data ?? [];
    },
    enabled: !!context.selectedClass && isPre,
  });

  const allStudents = isPre
    ? preRegStudents.map((s) => ({
        id: s.id,
        displayName: s.full_name || "",
        studentId: "",
        formIvIndex: s.form_iv_index,
      }))
    : signedUpStudents.map((s) => ({
        id: s.id,
        displayName: `${s.first_name} ${s.last_name}`,
        studentId: s.student_number,
        formIvIndex: s.form_iv_index,
      }));

  // For re-upload mode: fetch prior failing results to restrict the template to
  // only students who had FAIL/SUPP/INC/REPEAT modules in this Class/Semester/Year.
  const { data: failingResults = [] } = useQuery({
    queryKey: [
      "template-failing-results",
      context.selectedClass,
      context.semester,
      context.academicYear,
      context.examType,
    ],
    enabled:
      isReupload &&
      !!context.selectedClass &&
      !!context.semester &&
      !!context.academicYear,
    queryFn: async () => {
      // Server-side filter to keep the response small and avoid Supabase's
      // default 1000-row cap silently truncating SUPP students.
      const failingFilter =
        "remark.in.(FAIL,SUPP,INC,INCOMPLETE,REPEAT)," +
        "overall_remark.in.(FAIL,SUPP,INC,INCOMPLETE,REPEAT)," +
        "grade.in.(F,D,D+,D-,E,I,INC,SUPP)";
      const pageSize = 1000;
      const all: any[] = [];
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await supabase
          .from("exam_results")
          .select("student_id, form_iv_index, exam_type, grade, remark, overall_remark")
          .eq("semester", context.semester)
          .eq("academic_year", context.academicYear)
          .or(failingFilter)
          .range(from, from + pageSize - 1);
        if (error) throw error;
        const batch = data ?? [];
        all.push(...batch);
        if (batch.length < pageSize) break;
      }
      // Per-module fail check: use this row's own remark/grade only, NOT overall_remark
      // (overall_remark is a student-level "has any SUPP" flag and would mark passed
      // modules as failed).
      return all.filter((r: any) =>
        isFailingResult({ remark: r.remark, grade: r.grade })
      );
    },
  });

  // Normalise any module code shape into "PREFIX NUMBER" with a single space,
  // preserving leading zeros (e.g. "cmt05104" / "CMT  05104" -> "CMT 05104").
  const normaliseCode = (code: string | null | undefined): string => {
    if (!code) return "";
    const m = String(code).toUpperCase().replace(/\s+/g, "").match(/^([A-Z]{2,5})([0-9]{4,6})$/);
    if (!m) return String(code).toUpperCase().replace(/\s+/g, " ").trim();
    return `${m[1]} ${m[2]}`;
  };
  // Extract a module code like "CMT 05104" from an exam_type string like
  // "ES I - CMT 05104" (or variants). Preserves leading zero on the number.
  const extractModuleCode = (examType: string | null | undefined): string | null => {
    if (!examType) return null;
    const m = String(examType).toUpperCase().match(/([A-Z]{2,5})\s*([0-9]{4,6})/);
    if (!m) return null;
    return normaliseCode(`${m[1]}${m[2]}`);
  };

  const failingByStudent = new Map<string, Set<string>>();
  if (isReupload) {
    for (const r of failingResults as any[]) {
      const code = extractModuleCode(r.exam_type);
      if (!code) continue;
      const keys: string[] = [];
      if (r.student_id) keys.push(`id:${r.student_id}`);
      if (r.form_iv_index) keys.push(`fi:${String(r.form_iv_index).toUpperCase()}`);
      for (const k of keys) {
        if (!failingByStudent.has(k)) failingByStudent.set(k, new Set());
        failingByStudent.get(k)!.add(code);
      }
    }
  }

  const students = isReupload
    ? allStudents.filter((s) => {
        const fi = s.formIvIndex ? `fi:${String(s.formIvIndex).toUpperCase()}` : null;
        const id = s.id ? `id:${s.id}` : null;
        return (id && failingByStudent.has(id)) || (fi && failingByStudent.has(fi));
      })
    : allStudents;


  const { data: modules = [] } = useQuery({
    queryKey: ["template-modules", context.selectedClass, context.semester],
    queryFn: async () => {
      if (!context.selectedClass || !context.semester) return [];
      const { data } = await supabase
        .from("modules")
        .select("id, module_code, module_name")
        .eq("class", context.selectedClass)
        .eq("semester", context.semester)
        .order("module_code");
      return data ?? [];
    },
    enabled: !!context.selectedClass && !!context.semester,
  });

  const handleGenerate = async () => {
    if (!isReady) {
      toast({ title: "Incomplete setup", description: "Select all fields first", variant: "destructive" });
      return;
    }
    if (students.length === 0) {
      toast({
        title: "No students",
        description: isReupload
          ? `No students with prior FAIL/SUPP/INC/REPEAT results found for ${context.selectedClass} Sem ${context.semester} (${context.academicYear}).`
          : `No ${isPre ? "pre-registered" : "active"} students found for class ${context.selectedClass}`,
        variant: "destructive",
      });
      return;
    }

    setGenerating(true);
    try {
      const headers = [
        "SN",
        "Student ID",
        "Student Name",
        "Form IV Index",
        ...modules.map((m) => `${m.module_code}`),
        "GPA",
        "Remarks",
        "Modules to Retake",
      ];

      // For reupload: build a quick lookup of failed module codes per student.
      // failingByStudent already stores normalised module codes (e.g. "CMT 05104").
      const moduleCodeSet = new Set(modules.map((m) => normaliseCode(m.module_code)));
      const failedCodesFor = (s: { id: string; formIvIndex?: string | null }) => {
        if (!isReupload) return [] as string[];
        const idKey = `id:${s.id}`;
        const fiKey = s.formIvIndex ? `fi:${String(s.formIvIndex).toUpperCase()}` : null;
        const set = new Set<string>();
        for (const k of [idKey, fiKey].filter(Boolean) as string[]) {
          const codes = failingByStudent.get(k);
          if (!codes) continue;
          for (const c of codes) {
            const norm = normaliseCode(c);
            if (moduleCodeSet.has(norm)) set.add(norm);
          }
        }
        return Array.from(set);
      };

      const failedSetsByRow: Set<string>[] = [];
      const rows = students.map((s, i) => {
        const failedCodes = failedCodesFor(s);
        const failedSet = new Set(failedCodes);
        failedSetsByRow.push(failedSet);
        const row: Record<string, string | number> = {
          SN: i + 1,
          "Student ID": s.studentId,
          "Student Name": s.displayName,
          "Form IV Index": s.formIvIndex || "",
        };
        modules.forEach((m) => {
          // Both passed and failed cells start blank; passed cells will be locked below.
          row[m.module_code] = "";
        });
        row["GPA"] = "";
        row["Remarks"] = "";
        row["Modules to Retake"] = isReupload ? failedCodes.join(", ") : "";
        return row;
      });


      const filename = `Results_${context.selectedClass}_Sem${context.semester}_${context.examType.replace(" ", "")}_${context.academicYear.replace("/", "-")}.xlsx`;

      const metaRows: [string, string][] = [
        ["Field", "Value"],
        ["Class", context.selectedClass],
        ["Semester", String(context.semester)],
        ["Exam Type", context.examType],
        ["Academic Year", context.academicYear],
        ["Student Source", context.studentSource],
        ["Generated At", new Date().toISOString()],
        ["Total Students", students.length.toString()],
        ["Modules", modules.map((m) => m.module_code).join(", ")],
      ];

      if (isReupload) {
        // Re-upload template: use ExcelJS for proper per-cell protection + fills.
        const ExcelJS = (await import("exceljs")).default ?? (await import("exceljs"));
        const wb = new (ExcelJS as any).Workbook();
        const ws = wb.addWorksheet("Results");

        ws.columns = [
          { width: 5 },
          { width: 22 },
          { width: 28 },
          { width: 22 },
          ...modules.map(() => ({ width: 12 })),
          { width: 8 },
          { width: 15 },
          { width: 20 },
        ];

        // Legend row
        const legend = ws.addRow([
          "Legend: AMBER cells = SUPP / INC / REPEAT (enter new score). GREY cells = passed modules (locked). Blank cells in identity columns are read-only.",
        ]);
        ws.mergeCells(legend.number, 1, legend.number, headers.length);
        legend.getCell(1).font = { bold: true, color: { argb: "FF7A4F01" } };
        legend.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF3CD" } };
        legend.getCell(1).alignment = { vertical: "middle", horizontal: "left", wrapText: true };
        legend.height = 28;

        // Header row
        const headerRow = ws.addRow(headers);
        headerRow.eachCell((cell) => {
          cell.font = { bold: true };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE5E7EB" } };
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.border = {
            top: { style: "thin", color: { argb: "FF999999" } },
            bottom: { style: "thin", color: { argb: "FF999999" } },
          };
          cell.protection = { locked: true };
        });

        const moduleColStart = 5; // 1-based: SN=1, StudentID=2, Name=3, FormIV=4, modules start at 5
        const gpaCol = moduleColStart + modules.length;
        const remarksCol = gpaCol + 1;
        const retakeCol = remarksCol + 1;

        rows.forEach((r, rowIdx) => {
          const failedSet = failedSetsByRow[rowIdx];
          const values: any[] = [
            r.SN,
            r["Student ID"],
            r["Student Name"],
            r["Form IV Index"],
            ...modules.map((m) => r[m.module_code] ?? ""),
            r.GPA,
            r.Remarks,
            r["Modules to Retake"],
          ];
          const row = ws.addRow(values);

          // Identity columns — locked
          for (let c = 1; c <= 4; c++) {
            row.getCell(c).protection = { locked: true };
          }

          // Module cells
          modules.forEach((m, mIdx) => {
            const cell = row.getCell(moduleColStart + mIdx);
            const isFailed = failedSet.has(normaliseCode(m.module_code));
            if (isFailed) {
              cell.protection = { locked: false };
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF3CD" } };
              cell.border = {
                top: { style: "thin", color: { argb: "FFE0A800" } },
                left: { style: "thin", color: { argb: "FFE0A800" } },
                right: { style: "thin", color: { argb: "FFE0A800" } },
                bottom: { style: "thin", color: { argb: "FFE0A800" } },
              };
              cell.alignment = { horizontal: "center" };
            } else {
              cell.protection = { locked: true };
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEEEEEE" } };
            }
          });

          // GPA, Remarks, Modules to Retake — all unlocked per request
          row.getCell(gpaCol).protection = { locked: false };
          row.getCell(remarksCol).protection = { locked: false };
          row.getCell(retakeCol).protection = { locked: false };
        });

        await ws.protect("", {
          selectLockedCells: true,
          selectUnlockedCells: true,
          formatCells: false,
          insertRows: false,
          deleteRows: false,
        });

        // Metadata sheet
        const metaWs = wb.addWorksheet("Metadata");
        metaWs.columns = [{ width: 20 }, { width: 60 }];
        metaRows.forEach((r) => metaWs.addRow(r));
        metaWs.getRow(1).font = { bold: true };

        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        // Standard (non-reupload) template — keep existing xlsx path
        const XLSX = await import("xlsx");
        const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
        ws["!cols"] = [
          { wch: 5 },
          { wch: 22 },
          { wch: 28 },
          { wch: 22 },
          ...modules.map(() => ({ wch: 12 })),
          { wch: 8 },
          { wch: 15 },
          { wch: 20 },
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Results");
        const meta = XLSX.utils.aoa_to_sheet(metaRows);
        XLSX.utils.book_append_sheet(wb, meta, "Metadata");
        XLSX.writeFile(wb, filename);
      }
      toast({ title: "Template downloaded", description: filename });
    } catch (e: any) {
      toast({ title: "Error generating template", description: e.message, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileSpreadsheet className="h-4 w-4 text-primary" />
          Template Generator
        </CardTitle>
        <CardDescription>
          {isReupload
            ? `Re-upload template — amber cells are SUPP/INC/REPEAT (editable). Grey cells are passed modules and are locked.`
            : `Generate a prefilled Excel template with ${isPre ? "pre-registered" : "signed-up"} student and module data`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="text-sm text-muted-foreground">
            {students.length} students &middot; {modules.length} modules
            {modules.length === 0 && context.selectedClass && (
              <span className="text-amber-600 ml-2">⚠ No modules found — add modules for this class first</span>
            )}
          </div>
          <Button onClick={handleGenerate} disabled={!isReady || generating} className="ml-auto">
            {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Download Template
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
